import { useApolloClient } from '@apollo/client'
import { useFormikContext } from 'formik'
import React, { useCallback } from 'react'
import { flushSync } from 'react-dom'
import { useToasts } from 'react-toast-notifications'
import { GetPatientDocument, GetPatientQuery, GetPatientQueryVariables } from 'src/participant/patientQueries.generated'
import AutofillButton from 'src/participantHistory/autofill/AutofillButton'
import { CheckinCallNoteType } from './CheckinCallNoteSchema'
import { CHECK_IN_CALL_OUTSIDE_DASHBOARD } from './CheckinCallNoteFields'
import { getDummyCheckinCallNote } from './dummyCheckinCallNote'

type Props = Omit<React.ComponentProps<typeof AutofillButton>, 'onAutofill'> & {
  uid: string
  getDefaultValues: () => CheckinCallNoteType
  selectedCallID: string | null
  onSelectedCallIDChange: (callID: string | null) => void
}

export default function AutofillCheckinCallNoteButton({
  uid,
  getDefaultValues,
  selectedCallID,
  onSelectedCallIDChange,
  ...props
}: Props) {
  const client = useApolloClient()
  const { addToast } = useToasts()
  const { values, setValues } = useFormikContext<CheckinCallNoteType>()

  const autofill = useCallback(async () => {
    try {
      const { data } = await client.query<GetPatientQuery, GetPatientQueryVariables>({
        query: GetPatientDocument,
        variables: { uid },
      })
      const patient = data.patients?.[0]
      const dummyValues = getDummyCheckinCallNote(values, getDefaultValues())

      // onSubmit reads the linked call from the last render, so commit both before a double click submits.
      flushSync(() => {
        setValues({
          ...dummyValues,
          FIRST_NAME: patient?.firstname || dummyValues.FIRST_NAME,
          LAST_NAME: patient?.lastname || dummyValues.LAST_NAME,
        })
        if (!selectedCallID) {
          onSelectedCallIDChange(CHECK_IN_CALL_OUTSIDE_DASHBOARD)
        }
      })
      return true
    } catch (e) {
      addToast('Failed to autofill the check-in call note', { appearance: 'error' })
      return false
    }
  }, [addToast, client, getDefaultValues, onSelectedCallIDChange, selectedCallID, setValues, uid, values])

  return <AutofillButton onAutofill={autofill} {...props} />
}
